// src/oura/components/client/NoteComposer.tsx
//
// The Friday note (#3673 / backend T10). Short, weekly, staff to merchant.
// Sending it puts it at the top of the merchant's Home, in their bell and in
// their email (the server notifies every user on the client workspace).
//
// The last three sit under the box because the useful question when writing
// one is "what did I already say", not "show me the archive": the server
// returns exactly three for that reason.

import { useEffect, useRef, useState } from "react";
import { NotebookPenIcon } from "lucide-react";
import { useSnackbar } from "@hook/use-snackbar";
import { cn } from "../../lib/cn";
import { dayDate, joinDotted } from "../../lib/oura";
import { FoldCard, PillButton, REDUCED_MOTION, SURFACE, TYPE, useMediaQuery } from "@oura/ui";
import { listClientNotes, postClientNote, type ClientNote } from "@service/aeo/clientReview";
import { getNoteDraft } from "@service/aeo/delivery";
import { DELIVERY_ON } from "../../delivery/flag";
import { revealInCard } from "./staffScreen";

/** The merchant screen the newest note shows on (D1: the merchant's first page is "Home"). */
const MERCHANT_HOME = "Home";

export function NoteComposer({
    businessId,
    businessName,
    focusRequest = 0,
}: {
    businessId: string;
    businessName: string;
    /**
     * Raised by a link that opens the screen to write this week's note
     * (`?compose=note`): the box comes into view with focus in it. The caller
     * unfolds the card.
     */
    focusRequest?: number;
}) {
    const snackbar = useSnackbar();
    const reducedMotion = useMediaQuery(REDUCED_MOTION);
    const box = useRef<HTMLTextAreaElement>(null);
    const [notes, setNotes] = useState<ClientNote[]>([]);
    /** A failed read is not "no notes yet": the two are said differently. */
    const [read, setRead] = useState<"loading" | "done" | "failed">("loading");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    /**
     * THE TEXT that was confirmed, not merely that something was.
     *
     * A note is not a draft that can be tidied later: it lands at the top of
     * the merchant's Home, rings their bell and leaves as an email the moment
     * this resolves. There is no unsend. So the press asks first.
     *
     * A boolean was not enough (Jack, #3962). "Draft from this week" writes the
     * body from a request that may still be in flight when Send is pressed: the
     * draft lands after the confirm, the typing guard never fires because
     * nobody typed, and "Send it" mails words the staffer never read. Holding
     * the words themselves is what makes the confirm about the message rather
     * than about the click.
     *
     * The confirm is INLINE rather than a modal on purpose — the thing being
     * confirmed is the text itself, and a box over the middle of the screen
     * would cover the one thing worth re-reading before it goes out.
     */
    const [confirmed, setConfirmed] = useState<string | null>(null);
    const confirming = confirmed !== null;

    /**
     * ONE guard, not one per writer. Every path that can change the body —
     * typing, the draft, anything added later — withdraws the confirm by
     * passing through here, so a new writer cannot forget to.
     */
    useEffect(() => {
        if (confirmed !== null && body.trim() !== confirmed) setConfirmed(null);
    }, [body, confirmed]);
    const [drafting, setDrafting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setNotes([]);
        setRead("loading");
        void listClientNotes(businessId)
            .then((fetched) => {
                if (cancelled) return;
                // A note sent while this read was out is newer than anything
                // in it, so it stays first and the read only fills in behind
                // it; replacing the list would make a sent note vanish.
                setNotes((current) => {
                    const kept = new Set(current.map((n) => n.id));
                    return [...current, ...fetched.filter((n) => !kept.has(n.id))].slice(0, 3);
                });
                setRead("done");
            })
            .catch(() => {
                if (!cancelled) setRead("failed");
            });
        return () => { cancelled = true; };
    }, [businessId]);

    useEffect(() => {
        const el = box.current;
        if (focusRequest === 0 || !el) return;
        el.focus({ preventScroll: true });
        return revealInCard(el, !reducedMotion);
    }, [focusRequest, reducedMotion]);

    const send = async (text: string) => {
        // The words on screen must still be the words that were confirmed.
        // The effect above should have caught any drift; this is the one that
        // decides, because it runs at the moment of sending.
        if (!text || body.trim() !== text || sending) {
            setConfirmed(null);
            return;
        }
        setSending(true);
        setConfirmed(null);
        setError(null);
        try {
            const note = await postClientNote(businessId, text);
            setNotes((prev) => [note, ...prev].slice(0, 3));
            setBody("");
            snackbar.showSuccess("Note sent", `It is at the top of their ${MERCHANT_HOME}, in their bell and in their email.`);
        } catch {
            setError("The note was not sent. Try again in a moment.");
        } finally {
            setSending(false);
        }
    };

    const lastSent = notes[0] ? dayDate(notes[0].date) : null;

    return (
        <FoldCard
            id="note"
            title="This week's note"
            icon={{ icon: NotebookPenIcon, area: "people" }}
            foldedFact={lastSent ? `Last sent ${lastSent}` : undefined}
        >
            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                A short line to {businessName}. It goes to the top of their {MERCHANT_HOME}, to their bell, and by email.
            </p>
            <label htmlFor="oura-week-note" className="sr-only">This week&rsquo;s note</label>
            <textarea
                ref={box}
                id="oura-week-note"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                placeholder="e.g. 2 new pages went live this week, and Google now shows you for 2 more searches."
                className={cn(
                    TYPE.tb,
                    "mt-3 block w-full resize-y rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600",
                )}
            />
            <div className="mt-2 flex flex-wrap items-center gap-3">
                {confirming ? (
                    <>
                        <p role="status" className={cn(TYPE.tc, "m-0 basis-full text-gray-700")}>
                            This goes to {businessName} by email, to their bell and to the top of their {MERCHANT_HOME}. It cannot be unsent.
                        </p>
                        <PillButton onClick={() => setConfirmed(null)} disabled={sending}>Cancel</PillButton>
                        <PillButton variant="primary" busy={sending} onClick={() => void send(confirmed)}>
                            Send it
                        </PillButton>
                    </>
                ) : (
                    <PillButton variant="primary" busy={sending} disabled={!body.trim()} onClick={() => setConfirmed(body.trim())}>
                        Send
                    </PillButton>
                )}
                {/* The Friday note, drafted from the week's work in Delivery (H17): staff edit it, then send. */}
                {DELIVERY_ON && !confirming && (
                    <PillButton
                        busy={drafting}
                        onClick={() => {
                            setDrafting(true);
                            // Since the last note sent, or 7 days: a draft never repeats a note.
                            const lastNoteAt = notes.reduce<string | null>((m, n) => (!m || n.date > m ? n.date : m), null);
                            void getNoteDraft(businessId, businessName, lastNoteAt).then((r) => {
                                setDrafting(false);
                                if (r.ok) {
                                    // Never overwrite what staff already typed: the draft goes under it.
                                    const draft = r.data.draft;
                                    setBody((b) => (b.trim() ? `${b.replace(/\s+$/, "")}\n\n${draft}` : draft));
                                    setError(null);
                                    box.current?.focus();
                                } else setError(r.reason === "notFound" && r.message ? r.message : "The draft could not be made. Write it by hand.");
                            });
                        }}
                    >
                        Draft from this week
                    </PillButton>
                )}
                {error && <span className={cn(TYPE.tc, "text-rose-600")}>{error}</span>}
            </div>

            <div className="mt-5 flex flex-wrap items-baseline justify-between gap-x-3 border-t border-gray-100 pt-3">
                <p className={cn(TYPE.lab, "m-0")}>Past notes</p>
                <span className={cn(TYPE.tm, "text-gray-400")}>The client sees only the newest, on {MERCHANT_HOME}</span>
            </div>
            {notes.length > 0 && (
                <div className="mt-1">
                    {notes.map((n) => (
                        <div key={n.id} className={cn("border-t py-2.5 first:border-t-0", SURFACE.hairline)}>
                            <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{n.body}</p>
                            <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-400")}>{joinDotted(n.author, dayDate(n.date))}</p>
                        </div>
                    ))}
                </div>
            )}
            {read === "done" && notes.length === 0 && (
                <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-400")}>No notes yet.</p>
            )}
            {/* A note sent from here still lists above this: only the ones
                before it are unknown. */}
            {read === "failed" && (
                <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>
                    {notes.length > 0 ? "Earlier notes could not be read." : "Past notes could not be read."}
                </p>
            )}
        </FoldCard>
    );
}

